// Polling cadence for background-job status queries.
//
// Several views poll "is this job still running?" endpoints. Polling every 2s
// unconditionally — including while idle and, worse, while a job is wedged —
// means a dashboard left open steadily drains the API's in-process rate limit
// (300 requests / 60s per IP) and eventually trips a 429. These helpers make
// the cadence conditional: fast while a job is genuinely active, slow when
// idle, and backed off once a job looks stuck (the worker is almost certainly
// down, so there is no point hammering).

export const ACTIVE_POLL_MS = 2_000;
export const IDLE_POLL_MS = 15_000;
// A sync still pending/running after this long means the worker never picked it
// up (or died mid-run). The Dialpad users sync normally finishes in seconds.
export const SYNC_STUCK_AFTER_MS = 90_000;

/**
 * Poll fast while a job is active, slow when idle. Use as a Tanstack Query
 * `refetchInterval` for any status query whose payload carries `has_active`.
 */
export function activePollMs(hasActive: boolean | undefined): number {
  return hasActive ? ACTIVE_POLL_MS : IDLE_POLL_MS;
}

/**
 * True once an active sync has been queued/running longer than the stuck
 * threshold — i.e. the worker looks down. `queuedAt` is the server-reported
 * ISO timestamp the job was enqueued; `now` is `Date.now()` (injected for
 * testability).
 */
export function isSyncStuck(
  hasActive: boolean | undefined,
  queuedAt: string | null | undefined,
  now: number,
): boolean {
  if (!hasActive || !queuedAt) return false;
  const started = Date.parse(queuedAt);
  if (Number.isNaN(started)) return false;
  return now - started > SYNC_STUCK_AFTER_MS;
}

/**
 * Poll interval for a sync-status query that also backs off once the sync
 * looks stuck, so a down worker doesn't keep us hitting the API every 2s.
 */
export function syncPollMs(
  hasActive: boolean | undefined,
  queuedAt: string | null | undefined,
  now: number,
): number {
  if (!hasActive) return IDLE_POLL_MS;
  return isSyncStuck(hasActive, queuedAt, now) ? IDLE_POLL_MS : ACTIVE_POLL_MS;
}
